import './agent-view-indicators.component.ajscss';

import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ChannelUtil } from '@commons/util/helper/channel';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, IFilterService, ITimeoutService } from 'angular';
import { i18n } from 'i18next';

import {
  ActivityFacade,
  ActivityReason,
  ActivityType,
  ChanEnum,
  Channel,
  ChannelDirection,
  ChannelType,
  IMetricService,
  MetricId,
  Option,
} from '@contact/upgrade';

import templateUrl from './agent-view-indicators.component.ajs.html';

const MODULE_NAME = 'contactAgentViewIndicators';

class AgentViewIndicatorsCtrl implements IComponentController {
  public name = MODULE_NAME;

  channels: Channel[];
  channelStrs: string[];
  pauseReasonList: ActivityReason[];
  taskReasonList: ActivityReason[];
  metricIds: MetricId[];

  activityMetricLabels = [
    'TS_PRESENT_CSR', // agent_ts_present
    'TS_BREAK_CSR', // agent_ts_break
    'TS_TASK_CSR', // agent_ts_task
    'TS_AVAILABLE_CSR', // agent_ts_available
    'TS_WORKING_CSR', // agent_ts_working
    'SUM_BREAK_CSR', // agent_nb_break
    'SUM_EXTERNAL_OUTGOING_CALL_CSR', // agent_nb_unitary_phone_outgoing
  ];

  private metricAlignmentRight: { [oldLabel: string]: boolean } = {};
  private documentIdMap: { [metricIdStr: string]: string[] } = {};

  constructor(
    private readonly $filter: IFilterService,
    private readonly $i18next: i18n,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly metric: IMetricService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly activityFacade: ActivityFacade,
    private readonly $timeout: ITimeoutService
  ) {}

  $onInit(): void {
    this.channels = this.agentConfigDeprecatedService.channels.filter(channel => channel.type !== ChannelType.BASKET);
    this.channelStrs = this.channels.map(channel => ChannelUtil.toString(channel)).filter(this.distinct);

    this.activityFacade
      .getReasons(ActivityType.PAUSE)
      .pipe(ng1Destroy(this))
      .subscribe(reasons => (this.pauseReasonList = reasons));
    this.activityFacade
      .getReasons(ActivityType.TASK)
      .pipe(ng1Destroy(this))
      .subscribe(reasons => (this.taskReasonList = reasons));

    const associate = (metricIdStr: string, documentId: string) => {
      Option.of(this.documentIdMap[metricIdStr])
        .getOrElseFunc(() => (this.documentIdMap[metricIdStr] = []))
        .push(documentId);
    };

    const container = document.getElementById('indicateurActivite');
    if (container) {
      container.focus();
    }

    let entityId = this.sessionDeprecatedService.getProfile().id;

    let tsFormat = 'HH[h]mm[m]';
    let taFormat = 'mm[m]ss[s]';
    let resetHour = 0;
    let activityMetricIds = [
      MetricId.of('agent_ts_present')
        .withId(entityId)
        .since(resetHour)
        .withTimeFormat(tsFormat)
        .do(idStr => associate(idStr, 'TS_PRESENT_CSR')),
      MetricId.of('agent_ts_break')
        .withId(entityId)
        .since(resetHour)
        .withTimeFormat(tsFormat)
        .do(idStr => associate(idStr, 'TS_BREAK_CSR')),
      MetricId.of('agent_ts_task')
        .withId(entityId)
        .since(resetHour)
        .withTimeFormat(tsFormat)
        .do(idStr => associate(idStr, 'TS_TASK_CSR')),
      MetricId.of('agent_ts_available')
        .withId(entityId)
        .since(resetHour)
        .withTimeFormat(tsFormat)
        .do(idStr => associate(idStr, 'TS_AVAILABLE_CSR')),
      MetricId.of('agent_ts_working')
        .withId(entityId)
        .since(resetHour)
        .withTimeFormat(tsFormat)
        .do(idStr => associate(idStr, 'TS_WORKING_CSR')),
      MetricId.of('agent_nb_break')
        .withId(entityId)
        .since(resetHour)
        .do(idStr => associate(idStr, 'SUM_BREAK_CSR')),
      MetricId.of('agent_nb_unitary_phone_outgoing')
        .withId(entityId)
        .since(resetHour)
        .do(idStr => associate(idStr, 'SUM_EXTERNAL_OUTGOING_CALL_CSR')),
    ];

    const occupationRateMetricIds = this.channels.map(channel => {
      if (ChannelUtil.equals(channel, ChanEnum.In.Mail)) {
        return MetricId.of('agent_rate_occupation_mail_incoming_technical_contact')
          .withId(entityId)
          .addLiteralParam(ChannelType[channel.type])
          .addLiteralParam(ChannelDirection[channel.direction])
          .since(resetHour)
          .withRateFormat()
          .do(idStr => associate(idStr, this.occupationRateChannelLabel(ChannelUtil.toString(channel))));
      } else if (ChannelUtil.equals(channel, ChanEnum.Out.Mail)) {
        return MetricId.of('agent_rate_occupation_channel_technical_contact')
          .withId(entityId)
          .addLiteralParam(ChannelType[channel.type])
          .addLiteralParam(ChannelDirection[channel.direction])
          .since(resetHour)
          .withRateFormat()
          .do(idStr => associate(idStr, this.occupationRateChannelLabel(ChannelUtil.toString(channel))));
      } else {
        return MetricId.of('agent_rate_occupation_channel')
          .withId(entityId)
          .addLiteralParam(ChannelType[channel.type])
          .addLiteralParam(ChannelDirection[channel.direction])
          .since(resetHour)
          .withRateFormat()
          .do(idStr => associate(idStr, this.occupationRateChannelLabel(ChannelUtil.toString(channel))));
      }
    });

    let processingMetricIds: MetricId[] = [];

    this.channels.forEach(channel => {
      let channelStr = ChannelUtil.toString(channel);
      let metricSuffix = '';
      if (ChannelUtil.equals(channel, ChanEnum.Out.Mail)) {
        metricSuffix = '_technical_contact';
      }
      if (ChannelUtil.equals(channel, ChanEnum.In.Mail)) {
        processingMetricIds.push(
          MetricId.of('agent_ts_working_mail_incoming')
            .withId(entityId)
            .since(resetHour)
            .withTimeFormat(tsFormat)
            .do(idStr => associate(idStr, this.processingTimeLabel(channelStr))),
          MetricId.of('agent_ts_post_process_mail_incoming')
            .withId(entityId)
            .since(resetHour)
            .withTimeFormat(tsFormat)
            .do(idStr => associate(idStr, this.postProcessingTimeLabel(channelStr))),
          MetricId.of('agent_ta_in_process_mail_incoming')
            .withId(entityId)
            .since(resetHour)
            .withTimeFormat(taFormat)
            .do(idStr => associate(idStr, this.processingTimeAvgLabel(channelStr))),
          MetricId.of('agent_ta_post_process_mail_incoming')
            .withId(entityId)
            .since(resetHour)
            .withTimeFormat(taFormat)
            .do(idStr => associate(idStr, this.postProcessingTimeAvgLabel(channelStr))),
          MetricId.of('agent_nb_contact_processed_mail_incoming')
            .withId(entityId)
            .since(resetHour)
            .do(idStr => associate(idStr, this.processedContactsLabel(channelStr)))
        );
      } else {
        processingMetricIds.push(
          MetricId.of('agent_ts_working_channel' + metricSuffix)
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .withTimeFormat(tsFormat)
            .do(idStr => associate(idStr, this.processingTimeLabel(channelStr))),
          MetricId.of('agent_ts_post_process_channel' + metricSuffix)
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .withTimeFormat(tsFormat)
            .do(idStr => associate(idStr, this.postProcessingTimeLabel(channelStr))),
          MetricId.of('agent_ta_in_process_channel' + metricSuffix)
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .withTimeFormat(taFormat)
            .do(idStr => associate(idStr, this.processingTimeAvgLabel(channelStr))),
          MetricId.of('agent_ta_post_process_channel' + metricSuffix)
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .withTimeFormat(taFormat)
            .do(idStr => associate(idStr, this.postProcessingTimeAvgLabel(channelStr))),
          MetricId.of('agent_nb_contact_processed_channel' + metricSuffix)
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .do(idStr => associate(idStr, this.processedContactsLabel(channelStr)))
        );
      }
      if (channel.type !== ChannelType.VISIO) {
        processingMetricIds.push(
          MetricId.of('agent_nb_contact_not_answered_channel' + metricSuffix)
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .do(idStr => associate(idStr, this.notAnsweredContactsLabel(channelStr)))
        );
      }
      if (ChannelUtil.equals(channel, ChanEnum.In.Mail)) {
        processingMetricIds.push(
          MetricId.of('agent_mail_stat')
            .withId(entityId)
            .addLiteralParam('SUM_CONTACT_TRANSFERRED_MAIL_SENT_TO_AGENT_BY_AGENT')
            .do(idStr => associate(idStr, this.transferredContactsLabel(channelStr))),
          MetricId.of('agent_nb_forward_to_skills_mail_incoming')
            .withId(entityId)
            .since(resetHour)
            .do(idStr => associate(idStr, this.redirectedContactsLabel(channelStr)))
        );
      } else if (channel.type !== ChannelType.VISIO) {
        processingMetricIds.push(
          MetricId.of('agent_nb_forward_to_agent_channel')
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .do(idStr => associate(idStr, this.transferredContactsLabel(channelStr))),
          MetricId.of('agent_nb_forward_to_skills_channel')
            .withId(entityId)
            .addLiteralParam(ChannelType[channel.type])
            .addLiteralParam(ChannelDirection[channel.direction])
            .since(resetHour)
            .do(idStr => associate(idStr, this.redirectedContactsLabel(channelStr)))
        );
      }
    });

    let pauseTaskMetricIds: MetricId[] = [];

    if (!this.sessionDeprecatedService.hasPermission('CSR::simple_pause:request')) {
      this.pauseReasonList.forEach(pauseReason => {
        pauseTaskMetricIds.push(
          MetricId.of('agent_ts_break_reason')
            .withId(entityId)
            .addLiteralParam(pauseReason.id)
            .since(0)
            .withTimeFormat(tsFormat)
            .do(idStr => associate(idStr, this.pauseTaskMetricLabel(pauseReason)))
        );
      });
      this.$filter('orderBy')(this.pauseReasonList, 'label', false);
    } else {
      // we subscribe to the global time sum and use the default reason id for html id
      pauseTaskMetricIds.push(
        MetricId.of('agent_ts_break')
          .withId(entityId)
          .since(resetHour)
          .withTimeFormat(tsFormat)
          .do(idStr => associate(idStr, this.pauseTaskMetricLabel(this.pauseReasonList[0])))
      );
    }
    if (!this.sessionDeprecatedService.hasPermission('CSR::simple_task:request')) {
      this.taskReasonList.forEach(taskReason => {
        pauseTaskMetricIds.push(
          MetricId.of('agent_ts_break_reason')
            .withId(entityId)
            .addLiteralParam(taskReason.id)
            .since(0)
            .withTimeFormat(tsFormat)
            .do(idStr => associate(idStr, this.pauseTaskMetricLabel(taskReason)))
        );
      });
      this.$filter('orderBy')(this.taskReasonList, 'label', false);
    } else {
      // we subscribe to the global time sum and use the default reason id for html id
      pauseTaskMetricIds.push(
        MetricId.of('agent_ts_task')
          .withId(entityId)
          .since(resetHour)
          .withTimeFormat(tsFormat)
          .do(idStr => associate(idStr, this.pauseTaskMetricLabel(this.taskReasonList[0])))
      );
    }

    this.metricIds = activityMetricIds.concat(occupationRateMetricIds).concat(processingMetricIds).concat(pauseTaskMetricIds);

    this.metricIds.forEach(metricId => (this.metricAlignmentRight[metricId.documentId] = metricId.alignRight));
  }

  $postLink?(): void {
    this.metric
      .request(this.metricIds)
      .pipe(ng1Destroy(this))
      .subscribe(metric => {
        // Why a timeout of 0 : we need to run document.getElementById after the current $digest cycle has been completed.
        // This means using a $timeout with a delay of 0, which will be executed right after the current $digest cycle has ended.
        this.$timeout(() => {
          Option.of(this.documentIdMap[metric.metricId.toString()]).forEach(documentIds => {
            documentIds.forEach(docId =>
              Option.of(document.getElementById(docId)).forEach(doc => {
                doc.textContent = metric.formattedValue;
              })
            );
          });
        });
      });
  }

  occupationRateChannelLabel(channel: string): string {
    return `RATE_OCCUPATION_PRESENT_${channel}_CSR`; // agent_rate_occupation_channel
  }

  pauseTaskMetricLabel(pauseReason: ActivityReason): string {
    return `TS_PAUSE_REASON_${pauseReason.id.replace(/-/g, '_')}_CSR`; // agent_ts_break_reason
  }

  pauseTaskLabel(pauseReason: ActivityReason): string {
    return this.$i18next.t(`agent:pause.${pauseReason.label}`, { defaultValue: pauseReason.label });
  }

  processingTimeLabel(channel: string): string {
    return `TS_WORKING_${channel}_CSR`; // agent_ts_working_channel
  }

  postProcessingTimeLabel(channel: string): string {
    return `TS_POSTPROCESSING_${channel}_CSR`; // agent_ts_post_process_channel
  }

  processingTimeAvgLabel(channel: string): string {
    return `TA_IN_PROCESS_${channel}_CSR`; // agent_ta_in_process_channel
  }

  postProcessingTimeAvgLabel(channel: string): string {
    return `TA_POSTPROCESSING_${channel}_CSR`; // agent_ta_post_process_channel
  }

  processedContactsLabel(channel: string): string {
    return `SUM_CTT_SUCCESS_${channel}_CSR`; // agent_nb_contact_processed_channel
  }

  transferredContactsLabel(channel: string): string {
    return channel === 'MAIL_INCOMING' ? `SUM_CTT_TRANSFERRED_MAIL_SENT_CSR` : `SUM_CTT_TRANSFERRED_${channel}_CSR`;
  } // agent_nb_forward_to_agent_channel, agent_mail_stat(agtId, "SUM_CONTACT_TRANSFERRED_MAIL_SENT_TO_AGENT_BY_AGENT")

  redirectedContactsLabel(channel: string): string {
    return `SUM_CTT_REDIRECTED_${channel}_CSR`;
  } // agent_nb_forward_to_skills_channel

  scriptingRedirectedContactsLabel(channel: string): string {
    return channel === 'PHONE_INCOMING' ? `SUM_CTT_SCRIPT_ANCHOR_REDIRECTION_CSR` : `SUM_CTT_SCRIPT_ANCHOR_REDIRECTION_${channel}_CSR`;
  } // agent_nb_forward_to_script_anchor_channel

  notAnsweredContactsLabel(channel: string): string {
    return `SUM_CTT_NOT_ANSWERED_${channel}_CSR`; // agent_nb_contact_not_answered_channel
  }

  isNumber(metricLabel: string): boolean {
    return this.metricAlignmentRight[metricLabel];
  }

  distinct(value, index, self) {
    return self.indexOf(value) === index;
  }
}

export class AgentViewIndicators implements IComponentOptions {
  templateUrl = templateUrl;
  controller = [
    '$filter',
    '$i18next',
    'sessionDeprecatedService',
    'metric',
    'agentConfigDeprecatedService',
    'activityFacade',
    '$timeout',
    AgentViewIndicatorsCtrl,
  ];
}

export default MODULE_NAME;
